import { useEffect } from 'react';

/** Keep the edited field inside its dialog scroller after the visual viewport changes. */
export default function useDialogFocusVisibility(dialog) {
  useEffect(() => {
    if (!dialog) return undefined;
    const viewport = window.visualViewport;
    let frame;
    const reveal = () => {
      const field = document.activeElement;
      if (!dialog || !field || !dialog.contains(field)
        || !field.matches('input, textarea, select, [contenteditable="true"]')) return;
      for (let scroller = field.parentElement; scroller; scroller = scroller.parentElement) {
        if (scroller.clientHeight > 0 && scroller.scrollHeight > scroller.clientHeight
          && /auto|scroll/.test(window.getComputedStyle(scroller).overflowY)) {
          const area = scroller.getBoundingClientRect();
          const input = field.getBoundingClientRect();
          const inset = Math.min(12, area.height / 8);
          if (input.top < area.top + inset || input.height > area.height - inset * 2) {
            scroller.scrollTop += input.top - area.top - inset;
          } else if (input.bottom > area.bottom - inset) {
            scroller.scrollTop += input.bottom - area.bottom + inset;
          }
          break;
        }
        if (scroller === dialog) break;
      }
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      // Let the shell's visualViewport measurement and flex layout settle first.
      frame = requestAnimationFrame(() => { frame = requestAnimationFrame(reveal); });
    };
    document.addEventListener('focusin', schedule);
    window.addEventListener('resize', schedule);
    viewport?.addEventListener('resize', schedule);
    viewport?.addEventListener('scroll', schedule);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('focusin', schedule);
      window.removeEventListener('resize', schedule);
      viewport?.removeEventListener('resize', schedule);
      viewport?.removeEventListener('scroll', schedule);
    };
  }, [dialog]);
}
